import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../Login/AuthContext';
import './styles/index.css';
import './styles/dashboard/calendar.css';
import './styles/dashboard/mobile.css';
import HeaderComponent from './UserDashboard/HeaderComponent';
import UserReviewsTable from './UserDashboard/UserReviewsTable';
import UserEventsTable from './UserDashboard/UserEventsTable';
import EditReviewModal from './UserDashboard/EditReviewModal';
import EditEventModal from './UserDashboard/EditEventModal';
import SavedItemsList from './UserDashboard/SavedItemsList';
import CalendarView from './UserDashboard/CalendarView';
import EventBus from '../../utils/EventBus';
import FriendList from './UserDashboard/FriendList';
import BottomNavigation from './UserDashboard/BottomNavigation';
import UserCoupons from './UserDashboard/UserCoupons';
import { resetViewport } from '../../utils/viewportReset';

// Define API URL from env variable
const API_URL = process.env.REACT_APP_API_URL;

// Language translations
const translations = {
  th: {
    loading: 'กำลังโหลด...',
    savedItems: 'ที่บันทึกไว้',
    friendList: 'รายชื่อเพื่อน',
    calendar: 'ปฏิทินกิจกรรม',
    friends: 'เพื่อน',
    typeMessage: 'พิมพ์ข้อความ...',
    backToFriends: 'กลับไปรายชื่อเพื่อน',
    myReviews: 'รีวิวของฉัน',
    myEvents: 'กิจกรรมที่เพิ่ม'
  },
  en: {
    loading: 'Loading...',
    savedItems: 'Saved Items',
    friendList: 'Friend List',
    calendar: 'Event Calendar',
    friends: 'Friends',
    typeMessage: 'Type a message...',
    backToFriends: 'Back to Friends',
    myReviews: 'My Reviews',
    myEvents: 'My Events'
  },
  zh: {
    loading: '加载中...',
    savedItems: '已保存',
    friendList: '好友列表',
    calendar: '活动日历',
    friends: '好友',
    typeMessage: '输入消息...',
    backToFriends: '返回好友列表',
    myReviews: '我的评论',
    myEvents: '我发布的活动'
  }
};

// Loading Spinner component
const LoadingSpinner = ({ language = 'th' }) => (
  <div className="loading">
    <div className="loading-spinner"></div>
    <span>{translations[language].loading}</span>
  </div>
);

const UserDashboard = ({ user, onBack }) => {
  // Auth context for proper logout
  const { logout: authLogout } = useAuth();

  // State declarations
  const [participatingEvents, setParticipatingEvents] = useState({
    interested: [],
    going: []
  });
  const [friendsEvents, setFriendsEvents] = useState([]);
  const [friends, setFriends] = useState([]);
  const [userReviews, setUserReviews] = useState([]);
  const [userEvents, setUserEvents] = useState([]);
  const [editingReview, setEditingReview] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [requestPending, setRequestPending] = useState({
    artist: false,
    venueOwner: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'th');
  
  // Mobile states
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [activeTab, setActiveTab] = useState('saved');
  const [showChat, setShowChat] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState(null);

  // Refs and constants
  const navigate = useNavigate();
  const profileMenuRef = useRef(null);
  const t = translations[language];

  // Language change handler - FIXED: Defined early in component
  const handleLanguageChange = useCallback((newLanguage) => {
    console.log('🌐 Language changing from', language, 'to', newLanguage);
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
    setShowProfileMenu(false);
  }, [language]);

  // Helper function to sort events by date (newest first)
  const sortEventsByDate = useCallback((events) => {
    return [...events].sort((a, b) => {
      const dateA = new Date(a.event_date);
      const dateB = new Date(b.event_date);
      return dateB - dateA;
    });
  }, []);

  // Function to fetch participating events
  const fetchParticipatingEvents = useCallback(async () => {
    try {
      console.log('🔄 [Dashboard] Fetching participating events...');
      const participatingResponse = await axios.get(
        `${API_URL}/events/participating-events`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (participatingResponse.data) {
        const allEvents = participatingResponse.data;
        console.log('✅ [Dashboard] Participating events loaded:', allEvents.length);
        
        const interestedEvents = allEvents.filter(e => e.status === 'interested');
        const goingEvents = allEvents.filter(e => e.status === 'going');

        setParticipatingEvents({
          interested: sortEventsByDate(interestedEvents),
          going: sortEventsByDate(goingEvents)
        });
      }
    } catch (error) {
      console.error('❌ [Dashboard] Participating events API error:', error);
      setParticipatingEvents({
        interested: [],
        going: []
      });
    }
  }, [sortEventsByDate]);

  // Function to fetch friends' events
  const fetchFriendsEvents = useCallback(async () => {
    try {
      console.log('🔄 [Dashboard] Fetching friends\' events...');
      
      const response = await axios.get(`${API_URL}/friends/events`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      console.log('✅ [Dashboard] Friends events loaded:', response.data?.length || 0);

      if (response.data && Array.isArray(response.data)) {
        setFriendsEvents(response.data);
      } else {
        console.warn('⚠️ [Dashboard] Friends events response is not an array:', response.data);
        setFriendsEvents([]);
      }
    } catch (error) {
      console.error('❌ [Dashboard] Friends events API error:', error);
      setFriendsEvents([]);
    }
  }, []);

  // Function to fetch friends list
  const fetchFriends = useCallback(async () => {
    try {
      console.log('🔄 [Dashboard] Fetching friends...');
      const response = await axios.get(`${API_URL}/friends`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      const friendsData = response.data?.friends || [];
      const uniqueFriends = friendsData.filter((friend, index, self) => 
        index === self.findIndex(f => f.id === friend.id)
      );

      setFriends(uniqueFriends);
      console.log('✅ [Dashboard] Friends loaded:', uniqueFriends.length);
    } catch (error) {
      console.error('❌ [Dashboard] Friends API error:', error);
      setFriends([]);
    }
  }, []);

  // Function to fetch reviews created by the logged-in user
  const fetchUserReviews = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/reviews/user`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setUserReviews(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('❌ [Dashboard] User reviews API error:', error);
      setUserReviews([]);
    }
  }, []);

  // Function to fetch events created by the logged-in user
  const fetchUserEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/events/creator/${user.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setUserEvents(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('❌ [Dashboard] User events API error:', error);
      setUserEvents([]);
    }
  }, [user.id]);

  // Delete a user-owned review
  const handleDeleteReview = useCallback(async (id) => {
    try {
      await axios.delete(`${API_URL}/reviews/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setUserReviews(prev => prev.filter(r => r.id !== id));
    } catch (err) { console.error('❌ [Dashboard] Delete review error:', err); }
  }, []);

  // Delete a user-created event
  const handleDeleteEvent = useCallback(async (id) => {
    try {
      await axios.delete(`${API_URL}/events/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setUserEvents(prev => prev.filter(e => e.id !== id));
    } catch (err) { console.error('❌ [Dashboard] Delete event error:', err); }
  }, []);

  // Other handlers
  const handleLogout = useCallback(async () => {
    try {
      // Use AuthContext's logout function which properly clears all auth state
      await authLogout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if logout fails, navigate to login
      navigate('/login');
    }
  }, [authLogout, navigate]);

  const handleArtistRequest = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/users/request-role`, { role: 'artist' }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      setRequestPending(prev => ({ ...prev, artist: true }));
      alert('คำขอของคุณได้ถูกส่งไปยังผู้ดูแลระบบแล้ว');
    } catch (error) {
      console.error('Error requesting artist role:', error);
      alert('เกิดข้อผิดพลาด โปรดลองอีกครั้งในภายหลัง');
    }
  }, []);

  const handleVenueOwnerRequest = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/users/request-role`, { role: 'venue_owner' }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      setRequestPending(prev => ({ ...prev, venueOwner: true }));
      alert('คำขอของคุณได้ถูกส่งไปยังผู้ดูแลระบบแล้ว');
    } catch (error) {
      console.error('Error requesting venue owner role:', error);
      alert('เกิดข้อผิดพลาด โปรดลองอีกครั้งในภายหลัง');
    }
  }, []);

  const handleSearch = useCallback((e) => {
    e.preventDefault();
    navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
  }, [navigate, searchTerm]);

  // Friend selection handlers
  const handleFriendSelect = useCallback((friend) => {
    setSelectedFriend(friend);
    setShowChat(true);
  }, []);

  const handleBackToFriends = useCallback(() => {
    setShowChat(false);
    setSelectedFriend(null);
  }, []);

  // Render mobile content based on active tab
  const renderMobileContent = useCallback(() => {
    switch (activeTab) {
      case 'saved':
        return (
          <div className="mobile-tab-content">
            <div className="mobile-tab-header">
              <h3><i className="fas fa-bookmark"></i> {t.savedItems}</h3>
            </div>
            <SavedItemsList navigate={navigate} language={language} />
          </div>
        );
      case 'friends':
        return (
          <div className="mobile-tab-content">
            <div className="mobile-tab-header">
              <h3>
                {showChat && selectedFriend ? (
                  <>
                    <button 
                      className="back-button" 
                      onClick={handleBackToFriends}
                    >
                      <i className="fas fa-arrow-left"></i>
                    </button>
                    {selectedFriend?.name || 'แชท'}
                  </>
                ) : (
                  <>
                    <i className="fas fa-users"></i> {t.friendList}
                  </>
                )}
              </h3>
            </div>
            <FriendList 
              friends={friends}
              navigate={navigate}
              limit={10}
              user={user}
              language={language}
              onFriendSelect={handleFriendSelect}
              showChat={showChat}
              selectedFriend={selectedFriend}
              onBackToFriends={handleBackToFriends}
              isMobile={isMobile}
            />
          </div>
        );
      case 'calendar':
        return (
          <div className="mobile-tab-content">
            <div className="mobile-tab-header">
              <h3><i className="fas fa-calendar"></i> {t.calendar}</h3>
            </div>
            <div className="calendar-view">
              <div className="calendar-container">
                <CalendarView 
                  events={[...participatingEvents.interested, ...participatingEvents.going]} 
                  friendsEvents={friendsEvents}
                  onEventClick={(event) => navigate(`/event/${event.id}`)}
                  navigate={navigate}
                  language={language}
                />
              </div>
            </div>
          </div>
        );
      case 'coupons':
        return (
          <div className="mobile-tab-content">
            <div className="mobile-tab-header">
              <h3><i className="fas fa-ticket-alt"></i> คูปองของฉัน</h3>
            </div>
            <UserCoupons language={language} />
          </div>
        );
      default:
        return null;
    }
  }, [activeTab, t, showChat, selectedFriend, handleBackToFriends, friends, navigate, user, language, handleFriendSelect, isMobile, friendsEvents, participatingEvents]);

  // Effects
  useEffect(() => {
    // Reset viewport on mount (fixes iOS zoom persistence from login page)
    resetViewport();

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target) &&
          !event.target.closest('.profile-button')) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = EventBus.subscribe('eventStatusChanged', () => {
      console.log('🔄 [Dashboard] Event status changed, refreshing data...');
      fetchParticipatingEvents();
      fetchFriendsEvents();
    });
    
    return () => unsubscribe();
  }, [fetchParticipatingEvents, fetchFriendsEvents]);

  // Main data loading effect
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('🚀 [Dashboard] Starting optimized data fetch...');

        const requests = [
          axios.get(`${API_URL}/users/role-requests`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          }).catch(() => ({ data: { requests: [] } })),
          
          axios.get(`${API_URL}/events/search`, {
            params: {
              bl_latitude: -90,
              tr_latitude: 90,
              bl_longitude: -180,
              tr_longitude: 180,
              startDate: new Date().toISOString(),
              language: language.toUpperCase(),
              limit: 50
            }
          }),
          
          fetchParticipatingEvents(),
          fetchFriendsEvents(),
          fetchFriends(),
          fetchUserReviews(),
          fetchUserEvents()
        ];

        const [roleRequestResponse, eventsResponse] = await Promise.allSettled(requests);
        
        if (roleRequestResponse.status === 'fulfilled' && roleRequestResponse.value.data.requests) {
          setRequestPending({
            artist: roleRequestResponse.value.data.requests.includes('artist'),
            venueOwner: roleRequestResponse.value.data.requests.includes('venue_owner')
          });
        }

        if (eventsResponse.status === 'fulfilled') {
          console.log('✅ [Dashboard] Public events loaded for calendar:', eventsResponse.value.data?.length);
        }
        
      } catch (error) {
        console.error('❌ [Dashboard] Error fetching data:', error);
      } finally {
        setLoading(false);
        console.log('✅ [Dashboard] Optimized data fetch completed');
      }
    };

    fetchData();
  }, [fetchParticipatingEvents, fetchFriendsEvents, fetchFriends, fetchUserReviews, fetchUserEvents, language]);

  if (loading) {
    return <LoadingSpinner language={language} />;
  }

  return (
    <div className={`dashboard-container ${isMobile ? 'mobile-layout' : ''}`}>
      <HeaderComponent
        user={user}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleSearch={handleSearch}
        showProfileMenu={showProfileMenu}
        setShowProfileMenu={setShowProfileMenu}
        profileMenuRef={profileMenuRef}
        requestPending={requestPending}
        handleArtistRequest={handleArtistRequest}
        handleVenueOwnerRequest={handleVenueOwnerRequest}
        handleLogout={handleLogout}
        navigate={navigate}
        language={language}
        onLanguageChange={handleLanguageChange}
        onBack={onBack}
      />

      <div className="dashboard-content">
        {isMobile ? (
          <div className="mobile-content">
            {renderMobileContent()}
          </div>
        ) : (
          <div className="dashboard-main">
            <div className="dashboard-grid">
              <div className="grid-item" style={{ gridColumn: 'span 2' }}>
                <div className="dashboard-card">
                  <div className="card-header">
                    <h2><i className="fas fa-bookmark"></i> {t.savedItems}</h2>
                  </div>
                  <div className="card-body">
                    <SavedItemsList navigate={navigate} language={language} />
                  </div>
                </div>
              </div>

              <div className="grid-item">
                <div className="dashboard-card friend-chat-card">
                  <div className="card-body no-padding">
                    <FriendList 
                      friends={friends}
                      navigate={navigate}
                      limit={5}
                      user={user}
                      language={language}
                      onFriendSelect={handleFriendSelect}
                      showChat={showChat}
                      selectedFriend={selectedFriend}
                      onBackToFriends={handleBackToFriends}
                      isMobile={isMobile}
                    />
                  </div>
                </div>
              </div>

              <div className="grid-item grid-item-lg">
                <div className="dashboard-card">
                  <div className="card-header">
                    <h2><i className="fas fa-calendar"></i> {t.calendar}</h2>
                  </div>
                  <div className="card-body">
                    <CalendarView 
                      events={[...participatingEvents.interested, ...participatingEvents.going]} 
                      friendsEvents={friendsEvents}
                      onEventClick={(event) => navigate(`/event/${event.id}`)}
                      navigate={navigate}
                      language={language}
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="dashboard-card" style={{ margin: '0 0 24px 0', borderRadius: 12, border: '1px solid #eee', overflow: 'hidden' }}>
              <div className="card-header" style={{ padding: '14px 20px', borderBottom: '1px solid #f0f0f0', background: '#fffdf8' }}>
                <h2 style={{ margin: 0, fontSize: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <i className="fas fa-ticket-alt" style={{ color: '#f39c12' }}></i>
                  คูปองของฉัน
                </h2>
              </div>
              <div className="card-body" style={{ padding: '16px 20px' }}>
                <UserCoupons language={language} />
              </div>
            </div>

            <div className="copyright">
              <p>&copy; 2025 TVent. All rights reserved.</p>
            </div>
          </div>
        )}
      </div>

      {isMobile && (
        <BottomNavigation
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          language={language}
        />
      )}

      {editingReview && (
        <EditReviewModal
          open={true}
          review={editingReview}
          onClose={() => setEditingReview(null)}
          onSaved={(updated) => {
            setUserReviews(prev => prev.map(r => r.id === updated.id ? { ...r, ...updated } : r));
            setEditingReview(null);
          }}
          language={language}
        />
      )}

      {editingEvent && (
        <EditEventModal
          open={true}
          event={editingEvent}
          onClose={() => setEditingEvent(null)}
          onSaved={(updated) => {
            setUserEvents(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e));
            setEditingEvent(null);
          }}
          language={language}
        />
      )}
    </div>
  );
};

export default UserDashboard;